import {isValid, parseISO} from 'date-fns';
import {Segment} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useLocation} from 'react-router-dom';

import {isArray, isNilOrEmpty, isPlainObj, isString} from 'ramda-adjunct';

import {FINANCING_ENDING_BULK_ACTION_CODE} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes, customerRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, logError, queryParams} from '@dms/shared';

import {composePath, isApiDateString, useNavigate, useQueryState} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {FinancingEndingEndDateQuery} from '../../components/FinancingEndingEndDateQuery/FinancingEndingEndDateQuery';
import {FinancingEndingOperationOpener} from '../../components/FinancingEndingOperationOpener/FinancingEndingOperationOpener';
import type {FinancingEndingRowLabels} from '../../components/FinancingEndingSkipSummary/FinancingEndingSkipSummary';
import {FINANCING_ENDING_END_DATE_PERIOD} from '../../constants/FINANCING_ENDING_END_DATE_PERIOD';
import {FINANCING_ENDING_GRID_CODE} from '../../constants/FINANCING_ENDING_GRID_CODE';
import {useFinancingEndingSendingProgress} from '../../hooks/useFinancingEndingSendingProgress';
import {getSearchWithoutGrid} from '../../utils/getSearchWithoutGrid';
import {openFinancingEndingEmailDialog} from '../../utils/openFinancingEndingEmailDialog';
import {
  type FinancingEndingPeriod,
  type FinancingEndingPreset,
  getFinancingEndingPresetFilter,
} from './getFinancingEndingPresetFilter';

const PREDEFINED_COLUMNS = [
  'vehicleId',
  'customerId',
  'customerName',
  'vehicleTitle',
  'registrationPlate',
] as const;

const getPresetOptions = (): {value: FinancingEndingPreset; label: string}[] => [
  {
    value: 'next90Days',
    label: i18n.t('page.businessCases.financingEnding.presets.next90Days', {
      defaultValue: 'Next 90 days',
    }),
  },
  {
    value: 'next30Days',
    label: i18n.t('page.businessCases.financingEnding.presets.next30Days', {
      defaultValue: 'Next 30 days',
    }),
  },
  {
    value: 'thisMonth',
    label: i18n.t('general.labels.thisMonth', {defaultValue: 'This month'}),
  },
];

const OperationIdSchema = z.string().uuid();

// No period in the URL is the default tab: a reload or Back keeps the tab of the data query.
const getFinancingEndingPeriod = (period: string | null): FinancingEndingPeriod =>
  match(period)
    .with('next30Days', 'thisMonth', FINANCING_ENDING_END_DATE_PERIOD, (knownPeriod) => knownPeriod)
    .otherwise(() => 'next90Days');

const getTextCellValue = (row: Record<string, unknown>, column: string) => {
  const cell = row[column];
  return isPlainObj(cell) && 'value' in cell && isString(cell.value) ? cell.value : null;
};

// An entity cell holds `{id, label, ...}`; a plain string is the text cell an older backend sends.
const getEntityCellLabel = (row: Record<string, unknown>, column: string) => {
  const cell = row[column];
  if (!isPlainObj(cell) || !('value' in cell)) {
    return null;
  }
  const {value} = cell;
  if (isString(value)) {
    return value;
  }
  return isPlainObj(value) && 'label' in value && isString(value.label) ? value.label : null;
};

const getRowLabels = (rowData: Record<string, unknown> | Array<Record<string, unknown>>) =>
  (isArray(rowData) ? rowData : [rowData]).flatMap((row): FinancingEndingRowLabels[] =>
    isString(row.id)
      ? [
          {
            rowId: row.id,
            customerName: getEntityCellLabel(row, 'customerName'),
            vehicleTitle: getEntityCellLabel(row, 'vehicleTitle'),
            registrationPlate: getTextCellValue(row, 'registrationPlate'),
            endOfFinancing: getTextCellValue(row, 'endOfFinancing'),
          },
        ]
      : []
  );

// `parseDate` rolls `2026-02-30` over to March 2; `parseISO` rejects a day the month does not have.
const isValidEndDate = (value: string | null): value is string =>
  isApiDateString(value) && isValid(parseISO(value));

export function FinancingEndingList() {
  const navigate = useNavigate();
  const location = useLocation();
  // Replace, so Back does not return to the link entry and open the closed detail again.
  const [operationId, setOperationId, clearOperationId] = useQueryState(
    queryParams.BUSINESS_CASE_FINANCING_ENDING_OPERATION,
    true
  );
  const sendingProgress = useFinancingEndingSendingProgress({
    onOpenDetailByLink: setOperationId,
  });
  const [endDate] = useQueryState(queryParams.BUSINESS_CASE_FINANCING_ENDING_END_DATE);
  const [periodParam] = useQueryState(queryParams.BUSINESS_CASE_FINANCING_ENDING_PERIOD);
  const period = getFinancingEndingPeriod(periodParam);

  // One new history entry without the grid query, so the new tab mints a data query with its own
  // end date and Back returns to the previous tab with its query. The options keep Ctrl+click here.
  const changePeriod = (nextPeriod: FinancingEndingPeriod) => {
    const params = getSearchWithoutGrid(window.location.search, FINANCING_ENDING_GRID_CODE);
    if (nextPeriod === 'next90Days') {
      params.delete(queryParams.BUSINESS_CASE_FINANCING_ENDING_PERIOD);
    } else {
      params.set(queryParams.BUSINESS_CASE_FINANCING_ENDING_PERIOD, nextPeriod);
    }
    navigate({pathname: location.pathname, search: params.toString()}, {replace: false});
  };

  const parsedOperationId = OperationIdSchema.safeParse(operationId);
  const validOperationId = parsedOperationId.success ? parsedOperationId.data : null;

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    rowData,
    getColumnData,
    sourceSearchParams,
    queryId,
  }) => {
    const openPath = (path: string, isNewTab: boolean) =>
      isNewTab ? window.open(path) : navigate(path);

    match({actionKey, isBulk: isArray(rowId)})
      .with({actionKey: FINANCING_ENDING_BULK_ACTION_CODE, isBulk: true}, () => {
        if (isString(rowId)) {
          return logError(`Single invocation of bulk action ${actionKey} is not implemented`);
        }

        openFinancingEndingEmailDialog({
          rowIds: rowId,
          rowLabels: getRowLabels(rowData),
          dataQueryId: queryId,
          onSubmitted: sendingProgress.start,
        });
      })
      .with(
        {actionKey: 'detail', isBulk: false},
        {actionKey: 'detail_newTab', isBulk: false},
        () => {
          if (!isString(rowId)) {
            return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
          }

          openPath(
            composePath(businessCaseRoutes.overview, {
              params: {id: rowId},
              queryParams: sourceSearchParams,
              isSearchParamsPreserved: false,
            }),
            actionKey === 'detail_newTab'
          );
        }
      )
      .with(
        {actionKey: 'vehicleDetail', isBulk: false},
        {actionKey: 'vehicleDetail_newTab', isBulk: false},
        () => {
          const vehicleId = getColumnData<string>('vehicleId')?.value;

          if (isNilOrEmpty(vehicleId)) {
            return logError(`Missing vehicleId for action ${actionKey}`);
          }

          openPath(
            composePath(vehiclesRoutes.detail, {
              params: {id: vehicleId},
              isSearchParamsPreserved: false,
            }),
            actionKey === 'vehicleDetail_newTab'
          );
        }
      )
      .with(
        {actionKey: 'customerDetail', isBulk: false},
        {actionKey: 'customerDetail_newTab', isBulk: false},
        () => {
          const customerId = getColumnData<string>('customerId')?.value;

          if (isNilOrEmpty(customerId)) {
            return logError(`Missing customerId for action ${actionKey}`);
          }

          openPath(
            composePath(customerRoutes.detail, {
              params: {id: customerId},
              isSearchParamsPreserved: false,
            }),
            actionKey === 'customerDetail_newTab'
          );
        }
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const operationOpener = (
    <Show when={validOperationId}>
      {(validOperationId) => (
        <FinancingEndingOperationOpener
          key={validOperationId}
          operationId={validOperationId}
          openDetail={sendingProgress.openDetail}
          clearOperationId={clearOperationId}
        />
      )}
    </Show>
  );

  if (isValidEndDate(endDate)) {
    return (
      <Box height="100%">
        {operationOpener}
        <FinancingEndingEndDateQuery key={endDate} endDate={endDate} />
      </Box>
    );
  }

  return (
    <VStack spacing={3} height="100%">
      {operationOpener}
      <HStack spacing={4} align="center" wrap>
        <Segment<FinancingEndingPeriod>
          options={getPresetOptions()}
          value={period}
          onChange={changePeriod}
          label={i18n.t('page.businessCases.financingEnding.presets.label', {
            defaultValue: 'Financing end period',
          })}
          data-testid={testIds.businessCase.financingEnding('presets')}
        />
      </HStack>
      <Box flex={1} minHeight={0}>
        <DmsDataGrid
          key={period}
          gridCode={FINANCING_ENDING_GRID_CODE}
          predefinedColumns={PREDEFINED_COLUMNS}
          actionCallback={actionCallback}
          queryModifier={(filter) => getFinancingEndingPresetFilter(period, filter)}
          data-testid={testIds.businessCase.financingEnding('datagrid')}
        />
      </Box>
    </VStack>
  );
}
